<template>
    <div id="FormBuilder">
        <div class="header">
            <div class="header-text">
                <div :class="{ active: activeTab === 'create' }" @click="setActiveTab('create')">
                    {{ $t("form.create") }}
                </div>
                <div :class="{ active: activeTab === 'settings' }" @click="setActiveTab('settings')">
                    {{ $t("form.settings") }}
                </div>
                <div :class="{ active: activeTab === 'publish' }" @click="setActiveTab('publish')">
                    {{ $t("form.load") }}
                </div>
            </div>
        </div>
        <div v-if="activeTab === 'create'" id="form-builder">
            <div class="edit-menu">
                <div v-if="!openMenu" class="edit-menu-open" @click="openEditMenu">
                    {{ $t("form.insertFormField") }}
                    <span class="md-icon">add</span>
                </div>
                <transition name="slide">
                    <ItemMenu v-if="openMenu" class="item-menu" @close="closeEditMenu" />
                </transition>
            </div>
            <div class="edit-form-content" @dragover.prevent @drop="onDrop">
                <div v-if="form.logo.length" style="display:flex;justify-content: center; margin: auto; padding: 5px">
                    <div v-for="logo in form.logo" :key="logo._id" style="display: flex">
                        <img
                            :src="`https://static.supsis.live/${logo.path + logo._id}.${logo.ext}`"
                            :alt="logo.name"
                            :style="{ width: logo.width + 'px', height: logo.height + 'px', objectFit: 'contain' }"
                        />
                        <span
                            class="md-icon"
                            @click="deleteLogo(logo._id)"
                            style="color: #ff411cf5; cursor: pointer"
                            v-tippy="{ content: $t('form.clickToDeleteYourLogo'), placement: 'top' }"
                        >
                            delete
                        </span>
                        <span
                            class="md-icon"
                            style="cursor:pointer"
                            @click="showLogoSettingsModal = true"
                            v-tippy="{ content: $t('form.clickToDeleteYourLogo'), placement: 'top' }"
                        >
                            settings
                        </span>
                        <a-modal
                            v-model:visible="showLogoSettingsModal"
                            title="Logo Settings"
                            @ok="updateLogoDimensions"
                            @cancel="showLogoSettingsModal = false"
                        >
                            <div class="form-group">
                                <label for="logoHeight">Logo Height</label>
                                <input type="number" v-model="logoHeight" id="logoHeight" />
                            </div>
                            <div class="form-group">
                                <label for="logoWidth">Logo Width</label>
                                <input type="number" v-model="logoWidth" id="logoWidth" />
                            </div>
                        </a-modal>
                    </div>
                </div>
                <div class="add-logo" @click="openFileInput" v-else>
                    <div class="line"></div>
                    <input id="fileInput" type="file" style="display: none" @change="uploadFile" />
                    <div class="text">+ {{ $t("form.addYourLogo") }}</div>
                    <div class="line"></div>
                </div>
                <div class="form-edit">
                    <draggable v-model="formItems" @end="onEnd">
                        <div
                            v-for="(item, index) in formItems"
                            :key="index"
                            class="form-item"
                            :class="{ active: activeIndex === index }"
                            @click="setActive(index)"
                            :data-index="index"
                        >
                            <Title
                                v-if="item.type === 'title'"
                                :item="item"
                                :title-size="item.titleSize"
                                :title-align="item.titleAlign"
                                :title-text="item.titleText"
                                :sub-title="item.subTitle"
                                @update-field="updateField"
                            />
                            <FullName
                                v-if="item.type === 'fullName'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :full-name-align="item.fullNameAlign"
                                :first-name="item.firstName"
                                :last-name="item.lastName"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <Email
                                v-if="item.type === 'email'"
                                :item="item"
                                :field-email="item.fieldEmail"
                                :mail-align="item.mailAlign"
                                :alt-label="item.altLabel"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <Address
                                v-if="item.type === 'address'"
                                :field-address="item.fieldAddress"
                                :align="item.align"
                                :item="item"
                                :address-line1="item.addressLine1"
                                :address-line2="item.addressLine2"
                                :district="item.district"
                                :postal-code="item.postalCode"
                                :state="item.state"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <Phone
                                v-if="item.type === 'phone'"
                                :item="item"
                                :field-phone="item.fieldPhone"
                                :align="item.align"
                                :masked="item.masked"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <DatePicker
                                v-if="item.type === 'datePicker'"
                                :item="item"
                                :align="item.align"
                                :date-label="item.dateLabel"
                                :field-label="item.datePicker.fieldLabel"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <Meeting v-if="item.type === 'meeting'" />
                            <Signature v-if="item.type === 'signature'" />
                            <ListOfProducts v-if="item.type === 'listOfProducts'" />
                            <ShortText
                                v-if="item.type === 'shortText'"
                                :field-label="item.fieldLabel"
                                :item="item"
                                :align="item.align"
                                :alt-label="item.altLabel"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <LongText
                                v-if="item.type === 'longText'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :item="item"
                                :alt-label="item.altLabel"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <Paragraph v-if="item.type === 'paragraph'" />
                            <DropDownList
                                v-if="item.type === 'dropDownList'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :placeholder="item.placeholder"
                                :align="item.align"
                                :alt-label="item.altLabel"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <SingleChoice
                                v-if="item.type === 'singleChoice'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <MultipleSelection
                                v-if="item.type === 'multipleSelection'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <Number
                                v-if="item.type === 'number'"
                                :item="item"
                                :field-label="item.fieldLabel"
                                :align="item.labelAlign"
                                :sub-label="item.subLabel"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <FormImage
                                v-if="item.type === 'image'"
                                :item="item"
                                :align="item.align"
                                :asset="item.asset"
                                :imageWidth="item.imageWidth"
                                :imageHeight="item.imageHeight"
                            />
                            <FileUpload
                                v-if="item.type === 'fileUpload'"
                                :item="item"
                                :field-label-file="item.fieldLabelFile"
                                :align="item.align"
                                :alt-label-one="item.altLabelOne"
                                :alt-label-two="item.altLabelTwo"
                            />
                            <Hour
                                :item="item"
                                v-if="item.type === 'hour'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :hour-label="item.hourLabel"
                                :minute-label="item.minuteLabel"
                                @update-field="updateField"
                            />
                            <Spinner
                                :item="item"
                                v-if="item.type === 'spinner'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :is-required="item.isRequired"
                                @update-field="updateField"
                            />
                            <Send
                                v-if="item.type === 'send'"
                                :item="item"
                                :label="item.fieldLabel"
                                :align="item.align"
                            />
                            <DataTable v-if="item.type === 'dataTable'" />
                            <StarRating
                                :item="item"
                                v-if="item.type === 'starRating'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                @update-field="updateField"
                            />
                            <RatingScale
                                :item="item"
                                v-if="item.type === 'ratingScale'"
                                :field-label="item.fieldLabel"
                                :align="item.align"
                                :sub-label-one="item.subLabelOne"
                                :sub-label-two="item.subLabelTwo"
                                @update-field="updateField"
                            />
                            <SeparationElement
                                v-if="item.type === 'separationElement'"
                                :separation-color="item.separationColor"
                                :separator-bottom="item.separatorBottom"
                                :separator-height="item.separatorHeight"
                                :separator-side="item.separatorSide"
                                :separator-style="item.separatorStyle"
                                :separator-top="item.separatorTop"
                            />
                            <CollapseSection v-if="item.type === 'collapseSection'" />
                            <PageBreak v-if="item.type === 'pageBreak'" />
                            <div class="form-controls" :class="{ visible: activeIndex === index }">
                                <span class="md-icon" @click.stop="removeItem(index)">delete</span>
                                <span class="md-icon" @click.stop="openSettings(item, item.type)">settings</span>
                            </div>
                        </div>
                    </draggable>
                </div>
            </div>
            <div class="right-menu">
                <transition name="slideRight">
                    <div v-if="openSettingsMenu" class="settings-menu">
                        <SettingsMenu
                            :item="settingsMenuItem"
                            :type="settingsMenuType"
                            @close="openSettingsMenu = false"
                            @update-title-size="updateTitleSize"
                            @update-text-align="updateTextAlign"
                            @update-title-text="updateTitleText"
                            @update-subtitle-text="updateSubTitleText"
                            @update-field-label="updateFieldLabel"
                            @update-label-align="updateLabelAlign"
                            @update-first-name="updateFirstName"
                            @update-last-name="updateLastName"
                            @update-field-email="updateFieldEmail"
                            @update-label-align-mail="updateLabelAlignMail"
                            @update-alt-label-mail="updateAltLabelMail"
                            @update-field-label-address="updateFieldLabelAddress"
                            @update-label-align-address="updateLabelAlignAddress"
                            @update-field-address-line-1="updateFieldAddressLine1"
                            @update-field-address-line-2="updateFieldAddressLine2"
                            @update-field-district="updateFieldDistrict"
                            @update-field-postal-code="updateFieldPostalCode"
                            @update-field-state="updateFieldState"
                            @update-field-phone="updateFieldPhone"
                            @update-label-align-phone="updateLabelAlignPhone"
                            @update-masked-phone="updateMaskedPhone"
                            @update-field-label-date-picker="updateFieldLabelDatePicker"
                            @update-label-align-date-picker="updateLabelAlignDatePicker"
                            @update-date-label-date-picker="updateDateLabelDatePicker"
                            @update-field-label-short-text="updateFieldLabelShortText"
                            @update-label-align-short-text="updateLabelAlignShortText"
                            @update-alt-label-short-text="updateAltLabelShortText"
                            @update-field-label-long-text="updateFieldLabelLongText"
                            @update-label-align-long-text="updateLabelAlignLongText"
                            @update-alt-label-long-text="updateAltLabelLongText"
                            @update-field-label-drop-down-list="updateFieldLabelDropDownList"
                            @update-placeholder-drop-down-list="updatePlaceholderDropDownList"
                            @update-label-align-drop-down-list="updateLabelAlignDropDownList"
                            @update-alt-label-drop-down-list="updateAltLabelDropDownList"
                            @update-field-label-single-choice="updateFieldLabelSingleChoice"
                            @update-label-align-single-choice="updateLabelAlignSingleChoice"
                            @update-field-label-multiple-selection="updateFieldLabelMultipleSelection"
                            @update-label-align-multiple-selection="updateLabelAlignMultipleSelection"
                            @update-field-label-number="updateFieldLabelNumber"
                            @update-label-align-number="updateLabelAlignNumber"
                            @update-sub-label-number="updateSubLabelNumber"
                            @update-field-label-hour="updateFieldLabelHour"
                            @update-label-align-hour="updateLabelAlignHour"
                            @update-hour-label="updateHourLabel"
                            @update-minute-label="updateMinuteLabel"
                            @update-field-label-spinner="updateFieldLabelSpinner"
                            @update-label-align-spinner="updateLabelAlignSpinner"
                            @update-field-label-star-rating="updateFieldLabelStarRating"
                            @update-alignment-star-rating="updateAlignmentStarRating"
                            @update-field-label-rating-scale="updateFieldLabelRatingScale"
                            @update-field-label-send="updateFieldLabelSend"
                            @update-label-align-send="updateAlignmentSend"
                            @update-alignment-rating-scale="updateAlignmentRatingScale"
                            @update-sub-label-one-rating-scale="updateRatingScaleSubLabelOne"
                            @update-sub-label-two-rating-scale="updateRatingScaleSubLabelTwo"
                            @update-separation-color="updateSeparationColor"
                            @update-separator-style="updateSeparatorStyle"
                            @update-separator-height="updateSeparatorHeight"
                            @update-separator-bottom="updateSeparatorBottom"
                            @update-separator-top="updateSeparatorTop"
                            @update-separator-side="updateSeparatorSide"
                            @update-file-label="updateFileLabel"
                            @update-file-alt-label-one="updateFileAltLabelOne"
                            @update-file-alt-label-two="updateFileAltLabelTwo"
                            @update-file-align="updateFileAlign"
                            @update-required="updateRequired"
                            @update-image-url="updateImageUrl"
                            @update-image-width="updateImageWidth"
                            @update-image-height="updateImageHeight"
                            @update-image-align="updateImageAlign"
                        />
                    </div>
                </transition>
            </div>
        </div>
        <div v-if="activeTab === 'settings'">
            <Settings :form-id="formId" :site-id="site._id" />
        </div>
        <div v-if="activeTab === 'publish'">
            <Publish :form-id="formId" :site-id="site._id" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ItemMenu from "./ItemMenu.vue";
import draggable from "vuedraggable";
import SettingsMenu from "./ItemSettingsMenu/SettingsMenu.vue";
import Title from "./Item/Title.vue";
import FullName from "./Item/FullName.vue";
import Email from "./Item/Email.vue";
import Address from "./Item/Address.vue";
import Phone from "./Item/Phone.vue";
import DatePicker from "./Item/DatePicker.vue";
import Meeting from "./Item/Meeting.vue";
import Signature from "./Item/Signature.vue";
import ListOfProducts from "./Item/ListOfProducts.vue";
import ShortText from "./Item/ShortText.vue";
import LongText from "./Item/LongText.vue";
import Paragraph from "./Item/Paragraph.vue";
import DropDownList from "./Item/DropDownList.vue";
import SingleChoice from "./Item/SingleChoice.vue";
import MultipleSelection from "./Item/MultipleSelection.vue";
import Number from "./Item/Number.vue";
import FormImage from "./Item/FormImage.vue";
import FileUpload from "./Item/FileUpload.vue";
import Hour from "./Item/Hour.vue";
import Spinner from "./Item/Spinner.vue";
import Send from "./Item/Send.vue";
import DataTable from "./Item/DataTable.vue";
import StarRating from "./Item/StarRating.vue";
import RatingScale from "./Item/RatingScale.vue";
import SeparationElement from "./Item/SeparationElement.vue";
import CollapseSection from "./Item/CollapseSection.vue";
import PageBreak from "./Item/PageBreak.vue";
import Settings from "./FormSettings/Settings.vue";
import Publish from "./FormPublish/Publish.vue";
import { uploadAsset } from "../../../../api/assetUpload";
import axios from "axios";

export default {
    name: "FormBuilder",
    components: {
        ItemMenu,
        draggable,
        SettingsMenu,
        Title,
        FullName,
        Email,
        Address,
        Phone,
        DatePicker,
        Meeting,
        Signature,
        ListOfProducts,
        ShortText,
        LongText,
        Paragraph,
        DropDownList,
        SingleChoice,
        MultipleSelection,
        Number,
        FormImage,
        FileUpload,
        Hour,
        Spinner,
        Send,
        DataTable,
        StarRating,
        RatingScale,
        SeparationElement,
        CollapseSection,
        PageBreak,
        Settings,
        Publish,
    },
    props: {
        formId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            showLogoSettingsModal: false,
            logoHeight: null,
            logoWidth: null,
            activeTab: "create",
            openMenu: false,
            formItems: [],
            activeIndex: null,
            openSettingsMenu: false,
            settingsMenuType: "",
            settingsMenuItem: null,
            settingsMenuIndex: null,
            form: {
                logo: [],
            },
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
    },
    async mounted() {
        await this.loadForm();
    },
    methods: {
        ...mapActions({
            getFormById: "forms/getFormById",
            updateForm: "forms/updateForm",
            updateFormData: "forms/updateFormData",
            deleteFormField: "forms/deleteFormField",
        }),
        openLogoSettings(logo) {
            this.logoHeight = logo.height;
            this.logoWidth = logo.width;
            this.showLogoSettingsModal = true;
        },
        async updateLogoDimensions() {
            const updatedLogo = {
                id: this.form.logo[0]._id,
                height: this.logoHeight,
                width: this.logoWidth,
            };
            const updatedFormData = {
                ...this.form,
                logo: [updatedLogo],
            };
            try {
                await this.updateFormData({
                    siteId: this.form.siteId,
                    formId: this.form._id,
                    formData: updatedFormData,
                });
                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
                this.loadForm();
            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            } finally {
                this.showLogoSettingsModal = false;
            }
        },
        openFileInput() {
            document.getElementById("fileInput").click();
        },
        async loadForm(retryCount = 0) {
            const { siteId, formId } = this;
            if (!formId) {
                console.error("Form ID is null, cannot load form.");
                return;
            }
            try {
                const { form, formFields } = await this.getFormById({ siteId, formId });
                if (!form || !formFields) {
                    throw new Error("Form or formFields is null");
                }
                this.form = form;
                this.form.title = form.title;
                this.form.description = form.description;
                this.formItems = formFields
                    .map((field) => ({
                        ...field,
                        titleText: field.title?.titleText,
                        subTitle: field.title?.subTitle,
                        titleSize: field.title?.titleSize,
                        titleAlign: field.title?.titleAlign,
                        fieldLabel:
                            field.fullName?.fieldLabel ||
                            field.email?.fieldEmail ||
                            field.address?.fieldAddress ||
                            field.shortText?.fieldLabel ||
                            field.longText?.fieldLabel ||
                            field.number?.fieldLabel ||
                            field.hour?.fieldLabel ||
                            field.spinner?.fieldLabel ||
                            field.starRating?.fieldLabel ||
                            field.ratingScale?.fieldLabel ||
                            field.send?.fieldLabel ||
                            field.dropDownList?.fieldLabel ||
                            field.singleChoice?.fieldLabel ||
                            field.multipleSelection?.fieldLabel,
                        fullNameAlign: field.fullName?.fullNameAlign,
                        firstName: field.fullName?.firstName,
                        lastName: field.fullName?.lastName,
                        fieldEmail: field.email?.fieldEmail,
                        mailAlign: field.email?.mailAlign,
                        altLabel: field.email?.altLabel || field.shortText?.altLabel || field.longText?.altLabel,
                        fieldAddress: field.address?.fieldAddress,
                        addressLine1: field.address?.addressLine1,
                        addressLine2: field.address?.addressLine2,
                        district: field.address?.district,
                        postalCode: field.address?.postalCode,
                        state: field.address?.state,
                        align:
                            field.address?.align ||
                            field.phone?.align ||
                            field.datePicker?.align ||
                            field.shortText?.align ||
                            field.longText?.align ||
                            field.hour?.align ||
                            field.spinner?.align ||
                            field.starRating?.align ||
                            field.ratingScale?.align ||
                            field.send?.align ||
                            field.fileUpload?.align ||
                            field.dropDownList?.align ||
                            field.singleChoice?.align ||
                            field.multipleSelection?.align ||
                            field.image?.align,
                        fieldPhone: field.phone?.fieldPhone,
                        masked: field.phone?.masked,
                        fieldLabelDatePicker: field.datePicker?.fieldLabel,
                        dateLabel: field.datePicker?.dateLabel,
                        labelAlign: field.number?.labelAlign,
                        subLabel: field.number?.subLabel,
                        hourLabel: field.hour?.hourLabel,
                        minuteLabel: field.hour?.minuteLabel,
                        subLabelOne: field.ratingScale?.subLabelOne,
                        subLabelTwo: field.ratingScale?.subLabelTwo,
                        separationColor: field.separationElement?.separationColor,
                        separatorStyle: field.separationElement?.separatorStyle,
                        separatorHeight: field.separationElement?.separatorHeight,
                        separatorBottom: field.separationElement?.separatorBottom,
                        separatorTop: field.separationElement?.separatorTop,
                        separatorSide: field.separationElement?.separatorSide,
                        fieldLabelFile: field.fileUpload?.fieldLabelFile,
                        altLabelOne: field.fileUpload?.altLabelOne,
                        altLabelTwo: field.fileUpload?.altLabelTwo,
                        isRequired:
                            field.email?.isRequired ||
                            field.fullName?.isRequired ||
                            field.address?.isRequired ||
                            field.phone?.isRequired ||
                            field.datePicker?.isRequired ||
                            field.shortText?.isRequired ||
                            field.longText?.isRequired ||
                            field.dropDownList?.isRequired ||
                            field.singleChoice?.isRequired ||
                            field.multipleSelection?.isRequired ||
                            field.number?.isRequired ||
                            field.fileUpload?.isRequired ||
                            field.hour?.isRequired ||
                            field.spinner?.isRequired ||
                            field.starRating?.isRequired ||
                            field.ratingScale?.isRequired ||
                            field.send?.isRequired,
                        placeholder: field.dropDownList?.placeholder,
                        items: field.dropDownList?.items || field.singleChoice?.items || field.multipleSelection?.items,
                        asset: field.image?.asset,
                        imageWidth: field.image?.width,
                        imageHeight: field.image?.height,
                        order: field.order,
                        label: field.label,
                        type: field.type,
                    }))
                    .sort((a, b) => a.order - b.order);
            } catch (error) {
                console.error("Error loading form:", error);
                if (retryCount < 3) {
                    setTimeout(() => {
                        this.loadForm(retryCount + 1);
                    }, 1000);
                } else {
                    console.error("Failed to load form after 3 retries");
                }
            }
        },
        async saveForm() {
            const { siteId, formId } = this;
            const formData = {
                title: this.form.title,
                description: this.form.description,
            };
            const formFieldsData = this.formItems.map((item, index) => {
                const baseData = {
                    _id: item._id,
                    type: item.type,
                    label: item.label,
                    order: index,
                };
                switch (item.type) {
                    case "title":
                        return {
                            ...baseData,
                            title: {
                                titleText: item.titleText || "Başlık",
                                subTitle: item.subTitle || "Alt Başlık",
                                titleSize: item.titleSize || "default",
                                titleAlign: item.titleAlign || "top",
                            },
                        };
                    case "fullName":
                        return {
                            ...baseData,
                            fullName: {
                                fieldLabel: item.fieldLabel || "İsim",
                                fullNameAlign: item.fullNameAlign || "top",
                                firstName: item.firstName || "Ad",
                                lastName: item.lastName || "Soyad",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "email":
                        return {
                            ...baseData,
                            email: {
                                fieldEmail: item.fieldEmail || "E-posta",
                                mailAlign: item.mailAlign || "top",
                                altLabel: item.altLabel || "example@example.com",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "address":
                        return {
                            ...baseData,
                            address: {
                                fieldAddress: item.fieldAddress || "Addres",
                                align: item.align || "top",
                                addressLine1: item.addressLine1 || "Adres Satırı 1",
                                addressLine2: item.addressLine2 || "Adres Satırı 2",
                                district: item.district || "İlçe",
                                postalCode: item.postalCode || "Posta Kodu",
                                state: item.state || "Şehir",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "phone":
                        return {
                            ...baseData,
                            phone: {
                                fieldPhone: item.fieldPhone || "Telefon",
                                align: item.align || "top",
                                masked: item.masked || "Lütfen telefon numaranızı giriniz",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "datePicker":
                        return {
                            ...baseData,
                            datePicker: {
                                fieldLabel: item.fieldLabelDatePicker || "Tarih",
                                align: item.align || "top",
                                dateLabel: item.dateLabel || "Tarih Seçiniz",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "shortText":
                        return {
                            ...baseData,
                            shortText: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                altLabel: item.altLabel || "Cevabınızı buraya yazınız",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "longText":
                        return {
                            ...baseData,
                            longText: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                altLabel: item.altLabel || "Cevabınızı buraya yazınız",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "dropDownList":
                        return {
                            ...baseData,
                            dropDownList: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                altLabel: item.altLabel || "Seçim Yapınız...",
                                isRequired: item.isRequired || false,
                                items: item.items && item.items.length > 0 ? item.items : [],
                                placeholder: item.placeholder || "Seçim Yapınız...",
                            },
                        };
                    case "singleChoice":
                        return {
                            ...baseData,
                            singleChoice: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                isRequired: item.isRequired || false,
                                items: item.items && item.items.length > 0 ? item.items : [],
                            },
                        };
                    case "multipleSelection":
                        return {
                            ...baseData,
                            multipleSelection: {
                                fieldLabel: item.fieldLabel || "Bir soru girin",
                                align: item.align || "top",
                                isRequired: item.isRequired || false,
                                items: item.items && item.items.length > 0 ? item.items : [],
                            },
                        };
                    case "number":
                        return {
                            ...baseData,
                            number: {
                                fieldLabel: item.fieldLabel || "Bir sayı girin",
                                labelAlign: item.labelAlign || "top",
                                subLabel: item.subLabel || "Lütfen bir sayı giriniz",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "hour":
                        return {
                            ...baseData,
                            hour: {
                                fieldLabel: item.fieldLabel || "Saat",
                                align: item.align || "top",
                                hourLabel: item.hourLabel || "Saat",
                                minuteLabel: item.minuteLabel || "Dakika",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "spinner":
                        return {
                            ...baseData,
                            spinner: {
                                fieldLabel: item.fieldLabel || "Bir sayı girin",
                                align: item.align || "top",
                                isRequired: item.isRequired || false,
                            },
                        };
                    case "starRating":
                        return {
                            ...baseData,
                            starRating: {
                                fieldLabel: item.fieldLabel || "Bir sayı girin",
                                align: item.align || "top",
                            },
                        };
                    case "ratingScale":
                        return {
                            ...baseData,
                            ratingScale: {
                                fieldLabel: item.fieldLabel || "Bir sayı girin",
                                align: item.align || "top",
                                subLabelOne: item.subLabelOne || "Düşük",
                                subLabelTwo: item.subLabelTwo || "Yüksek",
                            },
                        };
                    case "send":
                        return {
                            ...baseData,
                            send: {
                                fieldLabel: item.fieldLabel || "Gönder",
                                align: item.align || "top",
                            },
                        };
                    case "separationElement":
                        return {
                            ...baseData,
                            separationElement: {
                                separationColor: item.separationColor || "#d3d3d3d3",
                                separatorStyle: item.separatorStyle || "solid",
                                separatorHeight: item.separatorHeight || "1",
                                separatorBottom: item.separatorBottom || "10",
                                separatorTop: item.separatorTop || "10",
                                separatorSide: item.separatorSide || "0",
                            },
                        };
                    case "fileUpload":
                        return {
                            ...baseData,
                            fileUpload: {
                                fieldLabelFile: item.fieldLabelFile || "Dosya Yükle",
                                align: item.align || "top",
                                altLabelOne: item.altLabelOne || "Dosya seçiniz",
                                altLabelTwo: item.altLabelTwo || "Dosya seçiniz",
                            },
                        };
                    case "image":
                        return {
                            ...baseData,
                            image: {
                                asset: item.asset || "https://static.supsis.live/common/email/imageDefault.png",
                                width: item.width || "400",
                                height: item.height || "300",
                                align: item.align || "center",
                            },
                        };
                    default:
                        return baseData;
                }
            });
            await this.updateForm({ siteId, formId, formData, formFieldsData });
        },
        async addItem(item, type, index) {
            item._id = null;
            item.order = index;
            item.label = type;
            item.type = type;
            this.formItems.splice(index, 0, item);
            this.formItems.forEach((item, idx) => {
                item.order = idx;
            });
            await this.saveForm();
            setTimeout(() => {
                this.loadForm();
            }, 200);
        },
        async removeItem(index) {
            if (this.formItems.length <= 1) {
                this.$toast.error("En Az 1 Alan Olmalıdır");
                return;
            }
            const [deleteFormField] = this.formItems.splice(index, 1);
            console.log("deleteFormField", deleteFormField);
            this.formItems.forEach((item, idx) => {
                item.order = idx;
            });
            await this.deleteFormField({ siteId: this.site._id, fieldId: deleteFormField._id });
            await this.saveForm();
        },
        setActiveTab(tab) {
            this.activeTab = tab;
        },
        openEditMenu() {
            this.openMenu = true;
        },
        closeEditMenu() {
            this.openMenu = false;
        },
        setActive(index) {
            this.activeIndex = index;
            this.openSettingsMenu = false;
        },
        openSettings(item, type) {
            this.settingsMenuType = type;
            this.settingsMenuItem = item;
            this.openSettingsMenu = true;
            console.log("Settings opened for:", {
                item,
                type,
            });
        },
        onDrop(event) {
            this.$feature.beforeCreateFormField(() => {
                const item = JSON.parse(event.dataTransfer.getData("item"));
                const dropIndex = this.getDropIndex(event.target);
                this.addItem(item, item.id, dropIndex);
            }, this.formItems.length);
        },
        getDropIndex(target) {
            const formItems = this.$el.querySelectorAll(".form-item");
            const dropTarget = target.closest(".form-item");
            if (dropTarget) {
                return Array.from(formItems).indexOf(dropTarget);
            }
            return this.formItems.length;
        },
        async onEnd() {
            this.formItems.forEach((item, index) => {
                item.order = index;
            });

            await this.saveForm();
        },
        updateTitleSize(size) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.titleSize = size;
            }
        },
        updateTextAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.titleAlign = align;
            }
        },
        updateTitleText(text) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.titleText = text;
            }
        },
        updateSubTitleText(text) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.subTitle = text;
            }
        },
        updateFieldLabel(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fullNameAlign = align;
            }
        },
        updateFirstName(name) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.firstName = name;
            }
        },
        updateLastName(name) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.lastName = name;
            }
        },
        updateFieldEmail(email) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldEmail = email;
            }
        },
        updateLabelAlignMail(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.mailAlign = align;
            }
        },
        updateAltLabelMail(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = label;
            }
        },
        updateFieldLabelAddress(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldAddress = label;
            }
        },
        updateLabelAlignAddress(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateFieldAddressLine1(address) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.addressLine1 = address;
            }
        },
        updateFieldAddressLine2(address) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.addressLine2 = address;
            }
        },
        updateFieldDistrict(district) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.district = district;
            }
        },
        updateFieldPostalCode(postalCode) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.postalCode = postalCode;
            }
        },
        updateFieldState(state) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.state = state;
            }
        },
        updateFieldPhone(phone) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldPhone = phone;
            }
        },
        updateLabelAlignPhone(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateMaskedPhone(masked) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.masked = masked;
            }
        },
        updateFieldLabelDatePicker(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlignDatePicker(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateDateLabelDatePicker(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.dateLabel = label;
            }
        },
        updateFieldLabelShortText(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlignShortText(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateAltLabelShortText(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = label;
            }
        },
        updateFieldLabelLongText(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlignLongText(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateAltLabelLongText(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = label;
            }
        },
        updateFieldLabelDropDownList(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updatePlaceholderDropDownList(placeholder) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.placeholder = placeholder;
            }
        },
        updateLabelAlignDropDownList(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateAltLabelDropDownList(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabel = label;
            }
        },
        updateFieldLabelSingleChoice(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlignSingleChoice(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateFieldLabelMultipleSelection(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlignMultipleSelection(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateFieldLabelNumber(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlignNumber(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.labelAlign = align;
            }
        },
        updateSubLabelNumber(subLabel) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.subLabel = subLabel;
            }
        },
        updateFieldLabelHour(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlignHour(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateHourLabel(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.hourLabel = label;
            }
        },
        updateMinuteLabel(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.minuteLabel = label;
            }
        },
        updateFieldLabelSpinner(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateLabelAlignSpinner(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateFieldLabelStarRating(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateAlignmentStarRating(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateFieldLabelSend(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateAlignmentSend(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateFieldLabelRatingScale(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabel = label;
            }
        },
        updateAlignmentRatingScale(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateRatingScaleSubLabelOne(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.subLabelOne = label;
            }
        },
        updateRatingScaleSubLabelTwo(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.subLabelTwo = label;
            }
        },
        updateSeparationColor(color) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separationColor = color;
            }
        },
        updateSeparatorStyle(style) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorStyle = style;
            }
        },
        updateSeparatorHeight(height) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorHeight = height;
            }
        },
        updateSeparatorBottom(bottom) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorBottom = bottom;
            }
        },
        updateSeparatorTop(top) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorTop = top;
            }
        },
        updateSeparatorSide(side) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.separatorSide = side;
            }
        },
        updateFileLabel(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.fieldLabelFile = label;
            }
        },
        updateFileAltLabelOne(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabelOne = label;
            }
        },
        updateFileAltLabelTwo(label) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.altLabelTwo = label;
            }
        },
        updateFileAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        updateRequired(required) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.isRequired = required;
            }
            setTimeout(() => {
                this.loadForm();
            }, 200);
        },
        updateImageUrl(url) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.asset = url;
            }
        },
        updateImageWidth(width) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.imageWidth = width;
            }
        },
        updateImageHeight(height) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.imageHeight = height;
            }
        },
        updateImageAlign(align) {
            if (this.settingsMenuItem) {
                this.settingsMenuItem.align = align;
            }
        },
        beforeUpload(file) {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png";
            if (!isJpgOrPng) {
                this.$toasted.show("Sadece Resim Yükleyebilirsiniz!", {
                    duration: 3000,
                });
            }
            const isLt5M = file.size / 1024 / 1024 < 50;
            if (!isLt5M) {
                this.$toasted.show("Resminizin Boyutu En Fazla 5MB Olmalıdır!", {
                    duration: 3000,
                });
            }
            return isJpgOrPng && isLt5M;
        },
        updateField() {
            setTimeout(() => {
                this.loadForm();
            }, 300);
        },
        async uploadFile(e) {
            let {
                files: [file],
            } = document.getElementById(e.target.id);
            if (!this.beforeUpload(file)) return;
            let path = `${this.site._id}/form/`;
            try {
                let imdUploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "form",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                await axios({
                    method: imdUploadData.data.method,
                    url: imdUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                const formData = {
                    logo: [imdUploadData.data.asset],
                };
                const assetId = imdUploadData.data.assetId;
                await this.updateFormData({
                    siteId: this.site._id,
                    formId: this.formId,
                    formData,
                });

                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
                this.loadForm();
            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            }
        },
        deleteLogo() {
            this.form.logo = null;
            this.updateFormData({
                siteId: this.site._id,
                formId: this.formId,
                formData: {
                    logo: [],
                },
            });
            this.loadForm();
        },
    },
    created() {
        this.loadForm();
    },
    watch: {
        formId: {
            immediate: true,
            handler(newFormId) {
                if (newFormId) {
                    this.loadForm();
                }
            },
        },
        item: {
            handler(newItem) {
                if (newItem && newItem.dropDownList) {
                    this.options = newItem.dropDownList.items || [];
                    this.placeholder = newItem.dropDownList.placeholder || "Seçim Yapınız...";
                }
                if (newItem && newItem.singleChoice) {
                    this.optionsRadio = newItem.singleChoice.items || [];
                }
            },
            immediate: true,
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
#FormBuilder {
    height: 100%;
    width: 100%;
    position: fixed;
    overflow: hidden;

    .header {
        background: linear-gradient(270deg, #ff6100 0%, #ffb841 99.97%);
        display: flex;
        align-items: center;
        padding: 0 20px;
        justify-content: center;
    }

    .header-text {
        display: flex;
        justify-content: center;
        align-items: center;
        color: #ffffff;
        font-size: 1.2rem;
        font-weight: 500;
    }

    .header-text div {
        padding: 10px 20px;
        cursor: pointer;
    }

    .header-text div:hover {
        background: rgba(255, 255, 255, 0.2);
    }

    .header-text .active {
        background: rgba(255, 255, 255, 0.5);
    }

    #form-builder {
        display: flex;
        //background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        height: 100%;
        justify-content: space-between;

        .edit-menu {
            position: relative;
            cursor: pointer;
            height: 100%;
            width: 0px;
            z-index: 999999 !important;

            .edit-menu-open {
                width: 200px;
                background: #22272d;
                color: #ffffff;
                padding: 15px;
                border-radius: 0px 30px 30px 0px;
                margin-top: 250px;
                display: flex;
                align-items: center;
            }

            .item-menu {
                position: relative;
                left: 0;
                top: 0;
                height: 100%;
                background: #ffffff;
            }
        }

        .right-menu {
            .settings-menu {
                position: relative;
                right: 110px;
                height: 100%;
                background: #ffffff;
                width: 100%;
                max-width: 320px;
            }
        }

        .edit-form-content {
            width: 900px;
            padding: 20px;
            overflow-y: auto;
            margin-bottom: 100px;

            .add-logo {
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-family: Arial, sans-serif;
                color: #6666aa;
                margin-bottom: 20px;

                .line {
                    border-top: 2px dashed #6666aa;
                    margin: 0 10px;
                    width: 100px;
                }

                .text {
                    white-space: nowrap;
                }
            }

            .form-edit {
                padding: 20px;
                background-color: #ffffff;
                border-radius: 10px;
                box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
                min-height: 500px;

                h1 {
                    margin: 0;
                    font-size: 1.5rem;
                    color: #333333;
                }

                h3 {
                    margin: 10px 0 20px;
                    font-size: 1.2rem;
                    color: #666666;
                }

                .form-item {
                    position: relative;
                    margin-bottom: 10px;
                    padding: 10px;
                    border: 1px solid transparent;
                    border-radius: 5px;
                    transition: border-color 0.3s;

                    &.active {
                        border-color: #3f51b5;
                    }

                    .form-controls {
                        position: absolute;
                        top: 50%;
                        right: -40px;
                        transform: translateY(-50%);
                        display: none;
                        flex-direction: column;

                        .md-icon {
                            background: #ffffff;
                            border-radius: 50%;
                            padding: 5px;
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                            cursor: pointer;

                            &:hover {
                                background: #f1f1f1;
                            }
                        }
                    }

                    .form-controls.visible {
                        display: flex;
                        gap: 10px;
                    }
                }
            }
        }
    }
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

.slideRight-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease-in-out;
}

.slideRight-enter .slideRight-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    font-weight: bold;
    margin-bottom: 0.5rem;
    color: #333;
}

.form-group input[type="number"] {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    color: #333;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: border-color 0.3s ease;
}

.form-group input[type="number"]:focus {
    border-color: #3f51b5;
    outline: none;
    box-shadow: 0 0 5px rgba(63, 81, 181, 0.5);
}

//.slideRight-leave-to {
//    animation: slideOut 0.3s forwards;
//}
</style>
